import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";

const CAMPAIGN_FIELDS = `
    id
    companyId
    companyName
    schoolId
    schoolName
    brokerId
    brokerName
    classes
    hpEventId
    active
    rnOnly
    leadQualityMin
    leadQualityMax
    startTime
    endTime
    hoursOldMin
    hoursOldMax
    weekdays
    whenStartId
    dailyCap
    monthlyCap
    costPerLead
    companyPostUrl
    description
    clientUrl
    username
    password
    appId
    apiUrl
    postConfirm
    isManaged
    isPortalRedirect
    fulfilledYesterday
    fulfilledToday
    fulfilledMtd
    fulfilledLastMonth
    ltHours { weekday startTime endTime }
    comments { date comment userName }
    assignedReps { id name }
    availableReps { id name }
`;

export async function fetchCampaign(id: number) {
    const q = gql`
        query q($id: Int!) {
            campaign(id: $id) {
                ${CAMPAIGN_FIELDS}
            }
        }
    `;
    const res = await query(q, {id});
    return res?.campaign || null;
}

export async function fetchCampaignWhenStarts() {
    const q = gql`
        query q {
            campaignWhenStarts { id name }
        }
    `;
    const res = await query(q, {});
    return res?.campaignWhenStarts || [];
}

export async function fetchCompaniesActive() {
    const q = gql`
        query q {
            companies { id name active }
        }
    `;
    const res = await query(q, {});
    return (res?.companies || []).filter((c: any) => !!c.active);
}

export async function searchSchoolsOptions(search: string) {
    const q = gql`
        query ($input: SchoolsInput!) {
            schools(input: $input) {
                items { id name }
            }
        }
    `;
    const res = await query(q, {
        input: {limit: 100, skip: 0, search, sort: [{column: "name", isAsc: true}]},
    });
    return (res?.schools?.items || []).map((s: any) => ({
        id: s.id,
        value: `${s.name} (${s.id})`,
        schoolId: s.id,
        schoolName: s.name,
    }));
}

export async function searchBrokersOptions(search: string) {
    const q = gql`
        query ($input: BrokersInput!) {
            brokers(input: $input) {
                items { id name }
            }
        }
    `;
    const res = await query(q, {
        input: {limit: 100, skip: 0, search, active: true, sort: [{column: "name", isAsc: true}]},
    });
    return (res?.brokers?.items || []).map((b: any) => ({
        id: b.id,
        value: `${b.name} (${b.id})`,
        brokerId: b.id,
        brokerName: b.name,
    }));
}

export async function searchPostEventOptions(search: string) {
    const q = gql`
        query ($input: PostEventsInput!) {
            postEvents(input: $input) {
                items {
                    id
                    schoolId
                    brokerId
                    schoolName
                    brokerName
                    programSetName
                    trafficLeadIdRequired
                }
            }
        }
    `;
    const res = await query(q, {
        input: {limit: 100, skip: 0, search},
    });
    return (res?.postEvents?.items || []).map((p: any) => ({
        id: p.id,
        value: `${p.schoolName}[${p.brokerName}][${p.id}][${p.programSetName}]`,
        schoolId: p.schoolId,
        brokerId: p.brokerId,
        schoolName: p.schoolName,
        brokerName: p.brokerName,
        trafficLeadIdRequired: p.trafficLeadIdRequired,
    }));
}

export async function getPostEventById(id: number) {
    const q = gql`
        query ($input: PostEventsInput!) {
            postEvents(input: $input) {
                items {
                    id
                    schoolId
                    brokerId
                    schoolName
                    brokerName
                    programSetName
                    trafficLeadIdRequired
                }
            }
        }
    `;
    const res = await query(q, {input: {limit: 1, skip: 0, ids: [id]}});
    const p = res?.postEvents?.items?.[0];
    if (!p) {
        return null;
    }
    return {
        id: p.id,
        value: `${p.schoolName}[${p.brokerName}][${p.id}][${p.programSetName}]`,
        schoolId: p.schoolId,
        brokerId: p.brokerId,
        schoolName: p.schoolName,
        brokerName: p.brokerName,
        trafficLeadIdRequired: p.trafficLeadIdRequired,
    };
}

export async function campaignEstimate(input: any) {
    const q = gql`
        query q($input: CampaignEstimateInput!) {
            campaignEstimate(input: $input)
        }
    `;
    const res = await query(q, {input});
    return res?.campaignEstimate ?? "";
}

export async function campaignUpsert(input: any) {
    const q = gql`
        mutation m($input: CampaignUpsertInput!) {
            campaignUpsert(input: $input) {
                ${CAMPAIGN_FIELDS}
            }
        }
    `;
    const res = await mutate(q, {input});
    return res?.campaignUpsert || null;
}

export async function campaignRepAdd(hpCampaignId: number, repId: number) {
    const q = gql`
        mutation m($hpCampaignId: Int!, $repId: Int!) {
            campaignRepAdd(hpCampaignId: $hpCampaignId, repId: $repId) {
                ${CAMPAIGN_FIELDS}
            }
        }
    `;
    const res = await mutate(q, {hpCampaignId, repId});
    return res?.campaignRepAdd || null;
}

export async function campaignRepRemove(hpCampaignId: number, repId: number) {
    const q = gql`
        mutation m($hpCampaignId: Int!, $repId: Int!) {
            campaignRepRemove(hpCampaignId: $hpCampaignId, repId: $repId) {
                ${CAMPAIGN_FIELDS}
            }
        }
    `;
    const res = await mutate(q, {hpCampaignId, repId});
    return res?.campaignRepRemove || null;
}

export const WEEKDAYS = [
    {bit: 1, label: "Mon"},
    {bit: 2, label: "Tue"},
    {bit: 4, label: "Wed"},
    {bit: 8, label: "Thu"},
    {bit: 16, label: "Fri"},
    {bit: 32, label: "Sat"},
    {bit: 64, label: "Sun"},
];

export function weekdayLabel(bit: number) {
    return WEEKDAYS.find((w) => w.bit === bit)?.label || String(bit);
}

export function halfHourTimes(includeEndOfDay = false): string[] {
    const out: string[] = [];
    for (let i = 0; i <= 23; i++) {
        const h = String(i).padStart(2, "0");
        out.push(`${h}:00`);
        out.push(`${h}:30`);
    }
    if (includeEndOfDay) {
        out.push("23:59:59");
    }
    return out;
}

export function quarterHourTimes(includeEndOfDay = false): string[] {
    const out: string[] = [];
    for (let i = 0; i <= 23; i++) {
        const h = String(i).padStart(2, "0");
        out.push(`${h}:00`);
        out.push(`${h}:15`);
        out.push(`${h}:30`);
        out.push(`${h}:45`);
    }
    if (includeEndOfDay) {
        out.push("23:59:59");
    }
    return out;
}

export function qualityOptions() {
    return Array.from({length: 101}, (_, i) => i);
}
